import React from 'react';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';

/**
 * Kolon arama: başka kolona geçince filtre değeri kalır (chip).
 * Escape → sadece edit kapanır; X → filtre temizlenir.
 */
export const openOrToggleColumnSearch = ({
  column,
  activeColumn,
  setActiveColumn,
  searches,
  setSearches
}) => {
  if (activeColumn === column) {
    setActiveColumn(null);
    return;
  }
  setActiveColumn(column);
  if (searches?.[column] === undefined) {
    setSearches((prev) => ({ ...prev, [column]: '' }));
  }
};

export const clearColumnSearchValue = ({
  column,
  activeColumn,
  setActiveColumn,
  setSearches
}) => {
  setSearches((prev) => {
    const next = { ...prev };
    delete next[column];
    return next;
  });
  if (activeColumn === column) {
    setActiveColumn(null);
  }
};

const labelText = (label) => {
  if (Array.isArray(label)) return label.filter(Boolean).join(' ');
  return label == null ? '' : String(label);
};

const ColumnSearchHeaderCell = ({
  label,
  searchValue = '',
  isSearchActive = false,
  isSorted = false,
  sortDirection = 'asc',
  onSort,
  onOpenSearch,
  onClearSearch,
  onSearchChange,
  onCloseSearchEdit,
  align = 'left',
  className = '',
  style,
  compact = false,
  showSort = true,
  showSearch = true
}) => {
  const text = labelText(label);
  const trimmed = String(searchValue || '').trim();
  const hasFilter = trimmed.length > 0;
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  const justifyClass =
    align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : '';
  const iconSize = compact ? 'h-3 w-3' : 'h-3.5 w-3.5';
  const inputPad = compact ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-1 text-xs sm:text-sm';

  return (
    <th
      className={`relative group ${alignClass} ${isSearchActive || hasFilter ? 'min-w-0' : ''} ${className}`}
      style={style}
    >
      {isSearchActive ? (
        <div className={`flex w-full min-w-0 items-center gap-1 ${justifyClass}`}>
          <input
            type="text"
            value={searchValue || ''}
            onChange={(e) => onSearchChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                onCloseSearchEdit?.();
              }
            }}
            autoFocus
            placeholder={`${text} ara...`}
            className={`min-w-0 max-w-[150px] rounded border border-emerald-500 bg-white font-normal normal-case tracking-normal text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-white ${inputPad}`}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (hasFilter) {
                onClearSearch?.();
              } else {
                onCloseSearchEdit?.();
              }
            }}
            className="shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title={hasFilter ? 'Filtreyi temizle' : 'Kapat'}
          >
            <X className={iconSize} />
          </button>
        </div>
      ) : hasFilter ? (
        <div className={`flex w-full min-w-0 items-center gap-1 ${justifyClass}`}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenSearch?.();
            }}
            className="inline-flex max-w-full min-w-0 items-center gap-1 rounded-full border border-emerald-500/70 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium normal-case tracking-normal text-emerald-800 hover:bg-emerald-100 dark:border-emerald-500/50 dark:bg-emerald-900/30 dark:text-emerald-200"
            title="Filtreyi düzenle"
          >
            <span className="truncate">{trimmed}</span>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClearSearch?.();
            }}
            className="shrink-0 rounded-full p-0.5 text-emerald-700 hover:bg-emerald-100 dark:text-emerald-300 dark:hover:bg-emerald-900/40"
            title="Filtreyi temizle"
          >
            <X className={iconSize} />
          </button>
          {(showSort || showSearch) && (
            <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
              {showSort && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSort?.();
                  }}
                  className="p-0.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                  title="Sırala"
                >
                  {isSorted ? (
                    sortDirection === 'asc' ? (
                      <ChevronUp className={iconSize} />
                    ) : (
                      <ChevronDown className={iconSize} />
                    )
                  ) : (
                    <ChevronUp className={`${iconSize} opacity-50`} />
                  )}
                </button>
              )}
              {showSearch && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenSearch?.();
                  }}
                  className="p-0.5 text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                  title="Ara"
                >
                  <Search className={iconSize} />
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className={`flex w-full min-w-0 items-center gap-1 ${justifyClass}`}>
          <span
            className={`min-w-0 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-100 ${
              align === 'right' ? '' : 'flex-1'
            }`}
            onClick={() => onSort?.()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSort?.();
              }
            }}
            role="button"
            tabIndex={0}
          >
            {Array.isArray(label)
              ? label.map((line, idx) => (
                  <span key={`${line}-${idx}`} className="block leading-tight">
                    {line}
                  </span>
                ))
              : label}
          </span>
          {(showSort || showSearch) && (
            <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
              {showSort && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSort?.();
                  }}
                  className="p-0.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                  title="Sırala"
                >
                  {isSorted ? (
                    sortDirection === 'asc' ? (
                      <ChevronUp className={iconSize} />
                    ) : (
                      <ChevronDown className={iconSize} />
                    )
                  ) : (
                    <ChevronUp className={`${iconSize} opacity-50`} />
                  )}
                </button>
              )}
              {showSearch && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenSearch?.();
                  }}
                  className="p-0.5 text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                  title="Ara"
                >
                  <Search className={iconSize} />
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </th>
  );
};

export default ColumnSearchHeaderCell;
